import { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import arbeiderUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

/**
 * PDF tegnet av oss, ikke av nettleseren.
 *
 * `<object>` og `<iframe>` overlater visningen til iOS, og da faar vi
 * hverken forhaandsvisning i kortet eller pinch-zoom — Safari nekter aa zoome
 * inne i en ramme, og gesten ender opp med aa bla sida bak i stedet.
 * CLAUDE.md krever «innebygd PDF-visning med pinch-zoom», saa sidene tegnes
 * paa lerret her.
 *
 * Det gir ogsaa det andre kravet gratis: ingen delingsknapp, ingen «aapne i
 * …», ingen nedlastingspil. Det finnes ingen innebygd leser aa faa dem fra.
 */

/* Arbeideren pakkes med appen framfor aa hentes fra et CDN. Appen skal
   virke offline, og en PDF som krever nett for aa TEGNES ville gjort
   nedlastingen meningsloes. */
pdfjs.GlobalWorkerOptions.workerSrc = arbeiderUrl;

export interface PdfProps {
  /** Blob-URL fra OPFS. Null mens fila ikke er hentet. */
  readonly url: string | null;
  /** Hvor mange sider som tegnes. Kortet viser bare den foerste. */
  readonly sider?: number | "alle";
  /** Skalering over basisbredden. 1 er «fyll bredden». */
  readonly zoom?: number;
  /**
   * Bredden zoom 1 betyr, i CSS-piksler.
   *
   * Maales av seg selv naar den ikke er oppgitt. Leseren i full skjerm MAA
   * oppgi den: der ligger sidene i en absolutt plassert boks, og en slik
   * boks har ingen bredde aa maale foer det foerste lerretet finnes.
   */
  readonly basis?: number;
  /**
   * Hvor mye tettere enn noedvendig sidene tegnes.
   *
   * Over 1 betyr at punktgrafikken har noe aa gaa paa naar brukeren zoomer:
   * da kan CSS forstoerre det som alt staar der, i stedet for at hele
   * dokumentet maa tegnes om foer teksten blir skarp igjen. Koster minne, og
   * settes derfor bare av leseren i full skjerm — kortet zoomer ikke.
   */
  readonly overskudd?: number;
  onSideantall?(n: number): void;
  /** Kalles naar alle sider staar i treet. Se forankringen i DokumentFlate. */
  onFerdig?(): void;
}

export function Pdf({
  url, sider = "alle", zoom = 1, basis: basisInn, overskudd = 1,
  onSideantall, onFerdig,
}: PdfProps): JSX.Element {
  const rot = useRef<HTMLDivElement | null>(null);
  /*
   * Basisbredden MAALES ÉN GANG og huskes.
   *
   * Her laa en tilbakekobling: bredden ble lest av beholderen ved hver
   * tegning, og et zoomet lerret gjoer beholderen bredere. Den nye bredden
   * ble saa grunnlaget for neste tegning, maalestokken vokste av seg selv,
   * og til slutt sprengte lerretet nettleserens stoerrelsesgrense — uten en
   * feil, bare et tomt felt.
   *
   * Maalt foer det foerste lerretet finnes er bredden flatas, og den er den
   * riktige: zoom 1 betyr «fyll skjermen».
   */
  const basisBredde = useRef(0);
  const [feil, setFeil] = useState<string | null>(null);

  // Ny fil, ny flate: maal bredden paa nytt.
  useEffect(() => { basisBredde.current = 0; }, [url]);

  useEffect(() => {
    const vert = rot.current;
    if (url === null || vert === null) return;
    let avbrutt = false;
    let dok: pdfjs.PDFDocumentProxy | null = null;

    void (async () => {
      try {
        dok = await pdfjs.getDocument({ url }).promise;
        if (avbrutt) return;
        onSideantall?.(dok.numPages);

        const antall = sider === "alle" ? dok.numPages : Math.min(sider, dok.numPages);

        /*
         * FOERSTE tegning fylles inn side for side. En NY tegning bygges
         * ferdig UTENFOR treet og byttes inn i ett grep.
         *
         * Her laa blinket. `replaceChildren()` toemte flata foer den nye
         * maalestokken var tegnet, saa dokumentet forsvant et oeyeblikk hver
         * gang fingrene slapp et klyp — og kom tilbake naar sidene var ferdige.
         * Bygges den nye utgaven utenfor treet, staar den gamle — med den
         * levende CSS-skaleringen paa — helt til den skarpe er klar.
         *
         * Foerste gang er det ingenting aa staa paa, og da er det omvendt:
         * foerste side skal vises med én gang framfor aa vente paa de
         * trettini bak den.
         */
        /*
         * AVSTANDEN MELLOM SIDENE MAA OGSAA ZOOME.
         *
         * Den staar i CSS som `--s-sm`, og en fast avstand er feil her: naar
         * en zoomet side vises ved aa SKALERE det som staar der, skaleres
         * avstanden med — men tegnes sidene paa nytt, gjoer den det ikke.
         * Forskjellen er liten, men den kommer akkurat i det punktgrafikken
         * byttes, og da ser den ut som et hopp. Zoomen gis til CSS, som
         * ganger avstanden med den.
         */
        vert.style.setProperty("--pdf-zoom", String(zoom));

        const bytter = vert.childElementCount > 0;
        const vokser: Node = bytter ? document.createDocumentFragment() : vert;

        for (let n = 1; n <= antall; n++) {
          const side = await dok.getPage(n);
          if (avbrutt) return;

          /*
           * Tegnes i ENHETENS pikslar, ikke i CSS-piksler.
           *
           * Uten `devicePixelRatio` blir teksten uskarp paa en telefon med
           * tett skjerm, og en dagsplan er smaa tall man skal kunne lese.
           * Zoomen ganges inn i tegningen framfor aa skaleres i CSS, av
           * samme grunn.
           */
          const basis = side.getViewport({ scale: 1 });
          if (basisBredde.current === 0) {
            basisBredde.current = basisInn ?? vert.clientWidth ?? 0;
            if (basisBredde.current === 0) basisBredde.current = 320;
          }

          // Hvor stor sida skal VISES. Dette er zoomen brukeren ba om.
          const visBredde = basisBredde.current * zoom;
          const visHoeyde = visBredde * (basis.height / basis.width);

          /*
           * Hvor stor den TEGNES er noe annet, og maa ha et tak.
           *
           * iOS har en arealgrense paa lerret, og et lerret over den blir
           * blankt — uten en feil, uten et unntak. Med `devicePixelRatio` 3
           * paa en iPhone naar man den fort: zoom 4 paa en side i A4 gir
           * rundt 28 millioner piksler.
           *
           * Taket senker TETTHETEN, ikke visningen. Da blir en kraftig
           * zoomet side litt mykere i kantene i stedet for aa forsvinne, og
           * det er en byttehandel man kan leve med.
           *
           * Taket deles ogsaa paa SIDEANTALLET. Et lerret paa tolv millioner
           * piksler er ~48 MB, og en kjoereplan paa tjue sider ville spist
           * hele telefonen. Korte dokumenter — en dagsplan er to sider — faar
           * fortsatt hele taket hver.
           */
          const tak = Math.min(12_000_000, Math.max(1_500_000, 36_000_000 / antall));
          let tetthet = Math.min(window.devicePixelRatio || 1, 3) * overskudd;
          while (tetthet > 1 && visBredde * tetthet * visHoeyde * tetthet > tak) {
            tetthet -= 0.5;
          }
          const utsnitt = side.getViewport({
            scale: (visBredde / basis.width) * tetthet,
          });

          const lerret = document.createElement("canvas");
          lerret.width = Math.ceil(utsnitt.width);
          lerret.height = Math.ceil(utsnitt.height);
          lerret.className = "pdf__side";
          /*
           * CSS-stoerrelsen foelger VISNINGEN, ikke tegningen — og den
           * AVRUNDES IKKE. Avrundet blir ikke hoeyden av to sider i firedobbel
           * maalestokk det samme som firedobbelen av to avrundede sider, og
           * den differansen viser seg som et rykk i det sidene byttes.
           */
          lerret.style.width = `${visBredde}px`;
          lerret.style.height = `${visHoeyde}px`;

          const flate = lerret.getContext("2d");
          if (flate === null) continue;
          await side.render({ canvas: lerret, canvasContext: flate, viewport: utsnitt }).promise;
          if (avbrutt) return;
          vokser.appendChild(lerret);
        }
        if (avbrutt) return;
        if (bytter) vert.replaceChildren(vokser);
        // Foerst naa staar sidene i sin endelige stoerrelse. Forankringen i
        // DokumentFlate regner paa den, og rydder bort CSS-skaleringen.
        onFerdig?.();
      } catch {
        if (avbrutt) return;
        setFeil("Fikk ikke åpnet dokumentet.");
        // Ogsaa en feil avslutter tegningen: uten dette staar den levende
        // skaleringen igjen paa en flate som aldri blir tegnet.
        onFerdig?.();
      }
    })();

    return () => {
      avbrutt = true;
      // `cleanup` og ikke `destroy`: pdf.js 6 frigjoer tegneressursene her,
      // og dokumentet selv samles naar ingen holder paa det.
      dok?.cleanup();
    };
  }, [url, sider, zoom, basisInn, overskudd, onSideantall, onFerdig]);

  if (feil !== null) {
    return <span className="pdf__feil mono">{feil}</span>;
  }
  return <div className="pdf" ref={rot} />;
}
